// ============================================================
//  PhishyAssistant — conversational AI scoped to ONE email
// ============================================================
//
// A floating chat widget the analyst opens from any analysis-result view.
// It answers questions about the email currently on screen ONLY — the
// `result` object is sent with every turn to /api/phishy/chat, and the
// backend constrains the model to that email's context. Reused by both the
// upload-based Phishing Analysis page and the Defender "User Submissions"
// view via the shared EmailAnalysisResult component.
//
// Props:
//   result     analysis result object (same shape returned by /api/phishing-analysis)
//   aiEnabled  whether an AI provider is available for this run (hides the
//              widget when AI was disabled / not configured)

import React, { useEffect, useRef, useState } from "react";
import { FaTimes, FaPaperPlane, FaSpinner } from "react-icons/fa";
import { RiGeminiFill } from "react-icons/ri";
import "./PhishyAssistant.css";

const SUGGESTIONS = [
  "Why was this email given this verdict?",
  "Is the sender spoofed? Explain SPF/DKIM/DMARC here.",
  "What should I check next?",
  "Summarise the riskiest indicators.",
];

export default function PhishyAssistant({ result, aiEnabled = true }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{ role, content }]
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // The investigated email is identified by its file name; when the analyst
  // switches to a different email, reset the conversation so context never
  // bleeds across emails.
  const emailKey = result?.fileName || null;
  useEffect(() => {
    setMessages([]);
    setError("");
    setInput("");
  }, [emailKey]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  if (!result || !aiEnabled) return null;

  const send = async (text) => {
    const content = String(text ?? input).trim();
    if (!content || sending) return;

    const nextMessages = [...messages, { role: "user", content }];
    setMessages(nextMessages);
    setInput("");
    setError("");
    setSending(true);

    try {
      const resp = await fetch("/api/phishy/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ result, messages: nextMessages }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) {
        throw new Error(data.error || `Request failed (${resp.status})`);
      }

      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setError(e.message || "Phishy couldn't respond. Try again.");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const subtitle = result.fileName ? `Investigating: ${result.fileName}` : "Email investigation";

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          className="phishy-fab"
          onClick={() => setOpen(true)}
          title="Ask Phishy about this email"
          aria-label="Open Phishy assistant"
        >
          <RiGeminiFill />
          <span className="phishy-fab-label">Ask Phishy</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="phishy-panel" role="dialog" aria-label="Phishy assistant">
          <div className="phishy-header">
            <div className="phishy-header-id">
              <span className="phishy-avatar">
                <RiGeminiFill />
              </span>
              <div className="phishy-header-text">
                <span className="phishy-title">Phishy</span>
                <span className="phishy-subtitle" title={subtitle}>
                  {subtitle}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="phishy-close"
              onClick={() => setOpen(false)}
              aria-label="Close Phishy"
            >
              <FaTimes />
            </button>
          </div>

          <div className="phishy-body" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="phishy-intro">
                <p className="phishy-intro-text">
                  Hi — I'm <strong>Phishy</strong>. I can only see and answer questions about{" "}
                  <strong>this email</strong>. Ask me anything about its headers, authentication,
                  URLs, attachments, content, or verdict.
                </p>
                <div className="phishy-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="phishy-chip"
                      onClick={() => send(s)}
                      disabled={sending}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`phishy-msg phishy-msg-${m.role}`}>
                {m.role === "assistant" && (
                  <span className="phishy-msg-avatar">
                    <RiGeminiFill />
                  </span>
                )}
                <div className="phishy-bubble">{m.content}</div>
              </div>
            ))}

            {sending && (
              <div className="phishy-msg phishy-msg-assistant">
                <span className="phishy-msg-avatar">
                  <RiGeminiFill />
                </span>
                <div className="phishy-bubble phishy-typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}

            {error && <div className="phishy-error">{error}</div>}
          </div>

          <div className="phishy-input-row">
            <textarea
              ref={inputRef}
              className="phishy-input"
              rows={1}
              placeholder="Ask about this email…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={sending}
            />
            <button
              type="button"
              className="phishy-send"
              onClick={() => send()}
              disabled={sending || !input.trim()}
              aria-label="Send"
            >
              {sending ? <FaSpinner className="phishy-spin" /> : <FaPaperPlane />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
